/**
 * Keyboard Shortcuts Modal
 *
 * Displays all available keyboard shortcuts grouped by category.
 * Triggered by Ctrl+/ or ? key.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { X, Search, Keyboard, Command, Navigation, MousePointer, Settings } from 'lucide-react';
import { useVisualHelp } from './VisualHelpProvider';
import { helpAPI } from '../../../api';

const CATEGORY_ICONS = {
  navigation: Navigation,
  actions: MousePointer,
  views: Command,
  general: Keyboard,
  settings: Settings,
};

const CATEGORY_LABELS = {
  navigation: 'Navigation',
  actions: 'Actions',
  views: 'Vues',
  general: 'General',
  settings: 'Parametres',
};

function ShortcutKey({ shortcut }) {
  // Parse shortcut like "ctrl+k" or "g then v"
  const parts = shortcut.split(/\s*\+\s*|\s+then\s+/i);
  const hasSequence = shortcut.toLowerCase().includes('then');

  return (
    <div className="flex items-center gap-1">
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {i > 0 && hasSequence && (
            <span className="text-slate-500 text-xs mx-1">puis</span>
          )}
          {i > 0 && !hasSequence && <span className="text-slate-500 text-xs">+</span>}
          <kbd className="px-2 py-1 text-xs font-semibold text-slate-300 bg-slate-700 border border-slate-600 rounded shadow-sm min-w-[24px] text-center">
            {part.charAt(0).toUpperCase() + part.slice(1)}
          </kbd>
        </React.Fragment>
      ))}
    </div>
  );
}

export function KeyboardShortcutsModal() {
  const { isShortcutsOpen, closeShortcuts } = useVisualHelp();
  const [shortcuts, setShortcuts] = useState({ shortcuts: [], grouped: {} });
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch shortcuts on open
  useEffect(() => {
    if (isShortcutsOpen) {
      setLoading(true);
      helpAPI.getShortcuts()
        .then((res) => {
          if (res.ok && res.data) {
            setShortcuts(res.data);
          } else {
            // Use fallback shortcuts
            setShortcuts({
              shortcuts: FALLBACK_SHORTCUTS,
              grouped: groupByCategory(FALLBACK_SHORTCUTS),
            });
          }
        })
        .catch((err) => {
          console.error('Failed to fetch shortcuts:', err);
          // Use fallback shortcuts
          setShortcuts({
            shortcuts: FALLBACK_SHORTCUTS,
            grouped: groupByCategory(FALLBACK_SHORTCUTS),
          });
        })
        .finally(() => setLoading(false));
    }
  }, [isShortcutsOpen]);

  // Filter shortcuts by search
  const filteredGrouped = useMemo(() => {
    if (!searchQuery.trim()) {
      return shortcuts.grouped;
    }

    const query = searchQuery.toLowerCase();
    const filtered = shortcuts.shortcuts.filter(
      (s) =>
        s.label?.toLowerCase().includes(query) ||
        s.description?.toLowerCase().includes(query) ||
        s.shortcut_key?.toLowerCase().includes(query)
    );

    return groupByCategory(filtered);
  }, [shortcuts, searchQuery]);

  // Handle escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isShortcutsOpen) {
        closeShortcuts();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isShortcutsOpen, closeShortcuts]);

  if (!isShortcutsOpen) return null;

  const categoryOrder = ['navigation', 'actions', 'views', 'general', 'settings'];
  const sortedCategories = Object.keys(filteredGrouped).sort(
    (a, b) => categoryOrder.indexOf(a) - categoryOrder.indexOf(b)
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={closeShortcuts}
      />

      {/* Modal */}
      <div
        className="relative bg-slate-800 rounded-xl shadow-2xl border border-slate-700 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Keyboard className="w-5 h-5 text-amber-400" />
            <h2 id="shortcuts-title" className="text-lg font-semibold text-white">
              Raccourcis Clavier
            </h2>
          </div>
          <button
            onClick={closeShortcuts}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher un raccourci..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
              autoFocus
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full" />
            </div>
          ) : sortedCategories.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              Aucun raccourci trouve pour "{searchQuery}"
            </div>
          ) : (
            <div className="space-y-6">
              {sortedCategories.map((category) => {
                const CategoryIcon = CATEGORY_ICONS[category] || Keyboard;
                const categoryShortcuts = filteredGrouped[category];

                return (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-3">
                      <CategoryIcon className="w-4 h-4 text-amber-400" />
                      <h3 className="text-sm font-medium text-slate-300 uppercase tracking-wide">
                        {CATEGORY_LABELS[category] || category}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {categoryShortcuts.map((shortcut) => (
                        <div
                          key={shortcut.action_code}
                          className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-slate-700/50 transition-colors"
                        >
                          <div>
                            <div className="text-white font-medium">{shortcut.label}</div>
                            {shortcut.description && (
                              <div className="text-sm text-slate-400">
                                {shortcut.description}
                              </div>
                            )}
                          </div>
                          <ShortcutKey shortcut={shortcut.shortcut_key} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-700 bg-slate-800/50">
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>
              Appuyez sur <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-xs">Esc</kbd> pour
              fermer
            </span>
            <span>{shortcuts.shortcuts.length} raccourcis</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper to group shortcuts by category
function groupByCategory(shortcuts) {
  return shortcuts.reduce((acc, s) => {
    const cat = s.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});
}

// Fallback shortcuts if API fails
const FALLBACK_SHORTCUTS = [
  {
    action_code: 'global_search',
    shortcut_key: 'ctrl+k',
    label: 'Recherche globale',
    description: 'Rechercher vehicules, conducteurs, etc.',
    category: 'navigation',
  },
  {
    action_code: 'keyboard_shortcuts',
    shortcut_key: 'ctrl+/',
    label: 'Raccourcis clavier',
    description: 'Afficher cette fenetre',
    category: 'general',
  },
  {
    action_code: 'help_center',
    shortcut_key: 'F1',
    label: "Centre d'aide",
    description: "Ouvrir le centre d'aide",
    category: 'general',
  },
  {
    action_code: 'nav_dashboard',
    shortcut_key: 'g then h',
    label: 'Tableau de bord',
    description: 'Aller au tableau de bord',
    category: 'views',
  },
  {
    action_code: 'nav_vehicles',
    shortcut_key: 'g then v',
    label: 'Vehicules',
    description: 'Aller aux vehicules',
    category: 'views',
  },
  {
    action_code: 'nav_drivers',
    shortcut_key: 'g then d',
    label: 'Conducteurs',
    description: 'Aller aux conducteurs',
    category: 'views',
  },
  {
    action_code: 'close_modal',
    shortcut_key: 'Escape',
    label: 'Fermer',
    description: 'Fermer la fenetre modale',
    category: 'general',
  },
];

export default KeyboardShortcutsModal;
